import Autolinker from "autolinker";
import moment from "moment";
import navigatorService from "./navigatorService";
import { NOTIFICATION_TYPES } from "../constants/DataTrackNotificationConstants";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../constants/PublicVariables";
import reduxStore from "../configureEnvStore";
import {
    addChatMessage,
    incrementUnreadChatMsgCount,
    resetUnreadChatMsgCount,
    setInComingChatBubbleMsg,
    setIsChatBubbleOpen,
    updateMessageTranslation,
    setMessageTranslating,
} from "../reducers/chat-reducer";
import azureTranslateService from "./azureTranslateService";
import chatSoundMP3 from "../assets/audio/chat_new_message.mp3";
import { ENABLED, FULL_DATE_FORMAT } from "../constants/Constants";
import { APIs } from "../constants/APIConstants";
import { EVENT_NAME, EXTRA_PARAM_TYPE } from "../constants/GTagConstants";
import { LogFirebase } from "./firestore";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY } from "../constants/FirebaseConstants";
import api from "../apis/api";
import googleEvent from "./googleEvent";
import Localize from "../locale/Localize";
import extractTextService from "./extractTextService";
import { sendDataTrack } from "../shared/dataTrackFunction";

class ChatService {
    autolinkerMessage(msg) {
        const autolinker = new Autolinker({
            urls: true,
            newWindow: true,
            email: true,
            phone: true,
            mention: false,
            hashtag: false,
            stripPrefix: false,
        });

        return autolinker.link(msg);
    }

    sanitizeHtmlTags(msg) {
        return msg ? msg.replace(/</g, "&lt;").replace(/>/g, "&gt;") : msg;
    }

    sendMessage(msg) {
        const currentDateTime = new Date();
        const formattedDateTime = moment.utc(currentDateTime, FULL_DATE_FORMAT).format(FULL_DATE_FORMAT);
        const timestampInSeconds = Math.floor(currentDateTime / 1000);

        const chatTextMessagePayload = {
            notification_type: NOTIFICATION_TYPES.CHAT_TEXT_MSG,
            identity: CALL_DATA.localIdentity,
            sender_id: parseInt(reduxStore.getState().commonReducer.ownIndex),
            device_type: navigatorService.getDeviceType(),
            message: msg,
            date_sent: formattedDateTime,
            msg_type: 1,
            timestamp: timestampInSeconds,
            messageId: Date.now(),
            mmid: CALL_DATA.memberId,
            guestName: GLOBAL_MEETING_DATA.isAgent
                ? Localize[PublicVariables.currentLanguageObj.languageCode]["participant"] +
                  " " +
                  reduxStore.getState().commonReducer.activeuserlist[CALL_DATA.localIdentity]
                : CALL_DATA.participantUserName,
        };
        sendDataTrack(chatTextMessagePayload);
        chatTextMessagePayload.textMsg = this.autolinkerMessage(this.sanitizeHtmlTags(msg));
        chatTextMessagePayload.guestName =
            chatTextMessagePayload.guestName ||
            Localize[PublicVariables.currentLanguageObj.languageCode]["participant"] + " " + chatTextMessagePayload.sender_id;

        reduxStore.dispatch(addChatMessage(chatTextMessagePayload));
        LogFirebase("LiveChat", "chat_message_sent", {
            message_id: chatTextMessagePayload.mmid + "_" + chatTextMessagePayload.timestamp,
            identity: chatTextMessagePayload.identity,
            date_time_sent: chatTextMessagePayload.date_sent,
        });

        googleEvent.logEvent(EVENT_NAME.CHAT_MESSAGE_SENT, EXTRA_PARAM_TYPE.obj, {
            MESSAGE_ID: chatTextMessagePayload.mmid + "_" + chatTextMessagePayload.timestamp,
            IDENTITY: chatTextMessagePayload.identity,
            DATE_TIME_SENT: chatTextMessagePayload.date_sent,
        });

        return chatTextMessagePayload;
    }

    /**
     * Send message with translation for agent
     * Translates the message to target language before sending
     * @param {string} msg - Original message text
     * @param {string} targetLanguage - Target language code for translation
     * @returns {Promise<object>} Chat message payload
     */
    async sendMessageWithTranslation(msg, targetLanguage) {
        const currentDateTime = new Date();
        const formattedDateTime = moment.utc(currentDateTime, FULL_DATE_FORMAT).format(FULL_DATE_FORMAT);
        const timestampInSeconds = Math.floor(currentDateTime / 1000);
        const messageId = Date.now();

        // Translate the message
        let translatedText = msg;
        let detectedLanguage = null;
        let isTranslated = false;

        try {
            const result = await azureTranslateService.translateMessage(msg, targetLanguage);
            if (result.isTranslated) {
                translatedText = result.translatedText;
                detectedLanguage = result.detectedLanguage;
                isTranslated = true;
            }
        } catch (error) {
            console.error("Translation error while sending:", error);
            LogFirebase(FIREBASE_EVENT_CATEGORY.ChatTranslation, FIREBASE_LOG_KEY.TranslationError, {
                error: error?.message || "Unknown error",
                targetLanguage: targetLanguage,
            });
        }

        // Create payload with translated message for guest
        const chatTextMessagePayload = {
            notification_type: NOTIFICATION_TYPES.CHAT_TEXT_MSG,
            identity: CALL_DATA.localIdentity,
            sender_id: parseInt(reduxStore.getState().commonReducer.ownIndex),
            device_type: navigatorService.getDeviceType(),
            message: translatedText, // Guest sees translated text
            date_sent: formattedDateTime,
            msg_type: 1,
            timestamp: timestampInSeconds,
            messageId: messageId,
            mmid: CALL_DATA.memberId,
            guestName: GLOBAL_MEETING_DATA.isAgent
                ? Localize[PublicVariables.currentLanguageObj.languageCode]["participant"] +
                  " " +
                  reduxStore.getState().commonReducer.activeuserlist[CALL_DATA.localIdentity]
                : CALL_DATA.participantUserName,
        };

        // Send translated message to guest via dataTrack
        sendDataTrack(chatTextMessagePayload);

        // For local display, store both original and translated
        const localChatPayload = {
            ...chatTextMessagePayload,
            textMsg: this.autolinkerMessage(this.sanitizeHtmlTags(translatedText)),
            originalText: isTranslated ? this.autolinkerMessage(this.sanitizeHtmlTags(msg)) : null,
            translatedText: isTranslated ? this.autolinkerMessage(this.sanitizeHtmlTags(translatedText)) : null,
            targetLanguage: targetLanguage,
            detectedLanguage: detectedLanguage,
            showOriginal: false,
            isAgentTranslatedMessage: isTranslated,
        };

        localChatPayload.guestName =
            localChatPayload.guestName || Localize[PublicVariables.currentLanguageObj.languageCode]["participant"] + " " + localChatPayload.sender_id;

        reduxStore.dispatch(addChatMessage(localChatPayload));

        LogFirebase("LiveChat", "chat_message_sent", {
            message_id: localChatPayload.mmid + "_" + localChatPayload.timestamp,
            identity: localChatPayload.identity,
            date_time_sent: localChatPayload.date_sent,
            translated: isTranslated,
            targetLanguage: targetLanguage,
        });

        googleEvent.logEvent(EVENT_NAME.CHAT_MESSAGE_SENT, EXTRA_PARAM_TYPE.obj, {
            MESSAGE_ID: localChatPayload.mmid + "_" + localChatPayload.timestamp,
            IDENTITY: localChatPayload.identity,
            DATE_TIME_SENT: localChatPayload.date_sent,
        });

        return localChatPayload;
    }

    handleIncomingMessage(msg) {
        msg.textMsg = this.autolinkerMessage(this.sanitizeHtmlTags(msg.message));
        msg.guestName =
            msg.guestName ||
            Localize[PublicVariables.currentLanguageObj.languageCode]["participant"] +
                " " +
                reduxStore.getState().commonReducer.activeuserlist[msg.identity];
        msg.messageId = Date.now();

        reduxStore.dispatch(addChatMessage(msg));
        const isChatDrawerOpen = reduxStore.getState().commonReducer.isChatDrawerOpen;

        if (isChatDrawerOpen) {
            reduxStore.dispatch(resetUnreadChatMsgCount());
            // Auto-translate incoming message if enabled
            this.translateMessageIfEnabled(msg);
        } else {
            reduxStore.dispatch(incrementUnreadChatMsgCount());
            this.playChatNewMessage();
            // Translate and show bubble with translated text if auto-translate is enabled
            this.translateAndShowBubble(msg);
        }

        const currentDateTime = new Date();
        const formattedDateTime = moment.utc(currentDateTime, FULL_DATE_FORMAT).format(FULL_DATE_FORMAT);
        const timestamp = Math.floor(currentDateTime / 1000);

        LogFirebase("LiveChat", "chat_message_received", {
            message_id: msg.mmid + "_" + timestamp,
            identity: CALL_DATA.localIdentity,
            date_time_received: formattedDateTime,
        });

        googleEvent.logEvent(EVENT_NAME.CHAT_MESSAGE_RECEIVED, EXTRA_PARAM_TYPE.obj, {
            MESSAGE_ID: msg.mmid + "_" + timestamp,
            IDENTITY: CALL_DATA.localIdentity,
            DATE_TIME_RECEIVED: formattedDateTime,
        });

        this.moveScrollToBottom();
    }

    /**
     * Translate message if auto-translate is enabled
     * @param {object} msg - Chat message object
     */
    async translateMessageIfEnabled(msg) {
        const { isAutoTranslateEnabled, selectedTranslationLanguage } = reduxStore.getState().chatReducer;

        if (!isAutoTranslateEnabled || !msg.message) {
            return;
        }

        // Set translating state
        reduxStore.dispatch(setMessageTranslating({ messageId: msg.messageId, isTranslating: true }));

        try {
            const result = await azureTranslateService.translateMessage(msg.message, selectedTranslationLanguage);

            // Update the message with translation
            reduxStore.dispatch(
                updateMessageTranslation({
                    messageId: msg.messageId,
                    translatedText: result.isTranslated ? this.autolinkerMessage(this.sanitizeHtmlTags(result.translatedText)) : null,
                    detectedLanguage: result.detectedLanguage,
                    isTranslating: false,
                    showOriginal: false,
                })
            );
        } catch (error) {
            console.error("Translation error:", error);
            LogFirebase(FIREBASE_EVENT_CATEGORY.ChatTranslation, FIREBASE_LOG_KEY.TranslationError, {
                error: error?.message || "Unknown error",
                targetLanguage: selectedTranslationLanguage,
            });
            reduxStore.dispatch(setMessageTranslating({ messageId: msg.messageId, isTranslating: false }));
        }
    }

    /**
     * Translate message and show chat bubble notification
     * Shows translated text in bubble if auto-translate is enabled
     * @param {object} msg - Chat message object
     */
    async translateAndShowBubble(msg) {
        const { isAutoTranslateEnabled, selectedTranslationLanguage } = reduxStore.getState().chatReducer;

        if (!isAutoTranslateEnabled || !msg.message) {
            // Show bubble with original message if auto-translate is disabled
            this.openChatBubble(msg);
            return;
        }

        // Set translating state
        reduxStore.dispatch(setMessageTranslating({ messageId: msg.messageId, isTranslating: true }));

        try {
            const result = await azureTranslateService.translateMessage(msg.message, selectedTranslationLanguage);

            // Update the message with translation in chat list
            reduxStore.dispatch(
                updateMessageTranslation({
                    messageId: msg.messageId,
                    translatedText: result.isTranslated ? this.autolinkerMessage(this.sanitizeHtmlTags(result.translatedText)) : null,
                    detectedLanguage: result.detectedLanguage,
                    isTranslating: false,
                    showOriginal: false,
                })
            );

            // Show bubble with translated text if translation was successful
            const bubbleMessage = result.isTranslated ? result.translatedText : msg.message;
            this.openChatBubble({ ...msg, message: bubbleMessage });
        } catch (error) {
            console.error("Translation error:", error);
            LogFirebase(FIREBASE_EVENT_CATEGORY.ChatTranslation, FIREBASE_LOG_KEY.TranslationError, {
                error: error?.message || "Unknown error",
                targetLanguage: selectedTranslationLanguage,
            });
            reduxStore.dispatch(setMessageTranslating({ messageId: msg.messageId, isTranslating: false }));
            // Show bubble with original message on error
            this.openChatBubble(msg);
        }
    }

    // when new msg arrive and chat model closed then play a sound
    playChatNewMessage() {
        const isChatDrawerOpen = reduxStore.getState().commonReducer.isChatDrawerOpen;

        // do not play sound
        if (isChatDrawerOpen) return;

        const audio = new Audio(chatSoundMP3);
        audio.play();
    }

    // open chat bubble model
    openChatBubble(msg) {
        let chatPopupTimer;
        // Cancel previous timer
        if (chatPopupTimer) {
            clearTimeout(chatPopupTimer);
        }

        // Set message and participant information
        const inComingChatBubbleMsg = {
            msg: msg.message,
            participantName: msg.guestName || Localize[PublicVariables.currentLanguageObj.languageCode]["participant"] + " " + msg.sender_id,
        };

        reduxStore.dispatch(setInComingChatBubbleMsg(inComingChatBubbleMsg));

        // Show chat popup
        reduxStore.dispatch(setIsChatBubbleOpen(true));

        // Set timeout to remove chat model
        chatPopupTimer = setTimeout(function () {
            reduxStore.dispatch(setIsChatBubbleOpen(false));
            clearTimeout(chatPopupTimer);
        }, 3000);
    }

    // while sending or receiving the msg, move scroll to the bottom
    moveScrollToBottom() {
        const chatSidebarBody = document.querySelector(".chat-sidebar-body");
        chatSidebarBody.scrollTop = chatSidebarBody.scrollHeight;
    }

    convertDataToBlob = function (chatMsgList) {
        let chatData = "";

        // Helper to strip HTML tags from autolinked text
        const stripHtml = (html) => (html ? html.replace(/<[^>]*>/g, "") : "");

        chatMsgList.forEach((chat) => {
            const chatTime = moment.utc(chat.date_sent, "DD-MM-YYYY hh:mm:ss A").local().format("hh:mm A");
            const senderInfo = chat.guestName
                ? `${chat.guestName}: `
                : `${Localize[PublicVariables.currentLanguageObj.languageCode]["from_participant"]} ${chat.sender_id}: `;

            // Check if message has translation
            let messageText = chat.message;
            if (chat.isAgentTranslatedMessage && chat.originalText) {
                // Agent's translated message: show translated with original
                const originalText = stripHtml(chat.originalText);
                messageText = `${chat.message} (Original - ${originalText})`;
            } else if (chat.translatedText && !chat.isAgentTranslatedMessage) {
                // Guest's auto-translated message: show translated with original
                const translatedText = stripHtml(chat.translatedText);
                messageText = `${translatedText} (Original - ${chat.message})`;
            }

            chatData += `${chatTime} ${senderInfo}${messageText}\n`;
        });

        const textToBLOB = new Blob([chatData], { type: "text/plain;charset=utf-8" });
        return textToBLOB;
    };

    createFileName = function (type) {
        const cleanDomainName = (GLOBAL_MEETING_DATA.domainInfo.domainName || "domain").replace(/\s+/g, " ");
        const cleanAgentName = (GLOBAL_MEETING_DATA.blitzzUserName || "agent").replace(/\s+/g, " ").replace(/[^\u4e00-\u9fa5a-zA-Z0-9-]/g, "");

        const currDate = moment.utc();
        const formattedDate = currDate.format("YYYY-MM-DD");
        const formattedTime = currDate.format("hh-mm-ss-A");

        let filename;

        if (PublicVariables.featureFlag.ENABLEFOLDERNAMING == ENABLED && PublicVariables.featureFlag.FILENAMEFORMAT != "") {
            const tempFileName = PublicVariables.featureFlag.FILENAMEFORMAT.replace("_{bookmark}", "");
            filename = `${tempFileName}_${formattedDate}-${formattedTime}_${type}.txt`;
        } else {
            const formattedTime = currDate.format("hh.mm.ssA");
            filename = `${cleanDomainName} ${cleanAgentName} ${GLOBAL_MEETING_DATA.sessionId} ${formattedDate} ${formattedTime} ${type}.txt`;
        }

        return filename;
    };

    // chat functionality ON & save chat history ON
    handleAutoDownloadChat() {
        if (GLOBAL_MEETING_DATA.isAgent) {
            const chatList = reduxStore.getState()?.chatReducer?.chatList || [];
            const chatMsgList = [...chatList].reverse();
            if (
                PublicVariables.featureFlag.SHOWMECHAT == ENABLED &&
                PublicVariables.featureFlag.SHOWMESAVECHAT == ENABLED &&
                chatMsgList.length > 0
            ) {
                LogFirebase("Auto_save_text_on_endcall", "chat", "");
                this.sendTxtFileToServer("chat", chatMsgList);
            }
        }
    }

    sendTxtFileToServer(type, list) {
        let url = null;
        let textToBLOB = null;
        const filename = this.createFileName(type);

        if (type === "chat") {
            url = APIs.STORE_CHAT_DATA.replace("{SessionId}", GLOBAL_MEETING_DATA.sessionId);
            textToBLOB = this.convertDataToBlob(list);
        } else if (type == "OCR") {
            url = APIs.STORE_OCR_DATA.replace("{SessionId}", GLOBAL_MEETING_DATA.sessionId);
            textToBLOB = extractTextService.convertOCRDataToBlob();
        }
        const fileObj = new File([textToBLOB], filename, { type: "text/plain;charset=utf-8" });

        const formData = new FormData();
        formData.append("file", fileObj);
        if (PublicVariables.featureFlag.FOLDERNAMEFORMAT && PublicVariables.featureFlag.ENABLEFOLDERNAMING == ENABLED) {
            formData.append("folderName", PublicVariables.featureFlag.FOLDERNAMEFORMAT);
        } else {
            formData.append("folderName", "");
        }
        formData.append("fileName", filename);

        api.postStorageWithoutHeader(url, formData)
            .then((result) => {
                console.log(result);
                LogFirebase("Auto_save_text_on_endcall", type, "uploaded successfully");
            })
            .catch((err) => {
                console.log(err);
                LogFirebase("Auto_save_text_on_endcall", type, "in error");
            });
    }
}

export default new ChatService();
